import { useMemo, useState } from 'react';

import { faAngleDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { cn } from '../lib/utils';

import { Button } from './button';
import { Checkbox } from './checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from './dropdown-menu';
import { Input } from './input';

export type SearchableCheckboxDropdownProps<T> = {
  items: T[];
  selectedItems: T[];
  onChange: (items: T[]) => void;
  getItemLabel: (item: T) => string;
  getItemValue: (item: T) => string;
  placeholder?: string;
  selectionPhrase?: string;
  buttonClassName?: string;
  searchPlaceholder?: string;
  noItemsFoundLabel?: string;
  includeAllOption?: boolean;
  isUnsorted?: boolean;
};

function createAllOption<T>(items: T[]): T | null {
  if (items.length === 0) return null;
  if (typeof items[0] === 'string') return 'All' as T;
  // For object types, create a special "All" object that matches the type
  return { id: 'all', name: 'All' } as T;
}

export const SearchableCheckboxDropdown = <T,>({
  items,
  selectedItems,
  onChange,
  getItemLabel,
  getItemValue,
  placeholder = 'Select items',
  selectionPhrase = 'item',
  buttonClassName = '',
  searchPlaceholder = 'Search...',
  noItemsFoundLabel = 'No items found',
  includeAllOption = false,
  isUnsorted = false,
}: SearchableCheckboxDropdownProps<T>) => {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const allOption = includeAllOption ? createAllOption(items) : null;

  const filteredItems = useMemo(() => {
    if (!search) return items;
    const searchLower = search.toLowerCase();
    return items.filter((item) =>
      getItemLabel(item).toLowerCase().includes(searchLower),
    );
  }, [items, search, getItemLabel]);

  const sortedItems = useMemo(() => {
    // Split items into selected and unselected groups
    const selected = filteredItems.filter((item) =>
      selectedItems.some(
        (selectedItem) => getItemValue(selectedItem) === getItemValue(item),
      ),
    );
    const unselected = filteredItems.filter(
      (item) =>
        !selectedItems.some(
          (selectedItem) => getItemValue(selectedItem) === getItemValue(item),
        ),
    );

    // Sort each group alphabetically
    const sortAlphabetically = (a: T, b: T) =>
      getItemLabel(a).localeCompare(getItemLabel(b));

    selected.sort(sortAlphabetically);
    unselected.sort(sortAlphabetically);

    // Combine the groups with selected items first
    let combined = !isUnsorted
      ? [...selected, ...unselected]
      : filteredItems.sort(sortAlphabetically);
    if (allOption) {
      combined = [allOption, ...combined];
    }
    return combined;
  }, [
    filteredItems,
    selectedItems,
    getItemLabel,
    getItemValue,
    allOption,
    isUnsorted,
  ]);

  const isAllSelected = includeAllOption && selectedItems.length === 0;

  const handleChange = (item: T) => {
    if (
      includeAllOption &&
      allOption &&
      getItemValue(item) === getItemValue(allOption)
    ) {
      // If "All" is already selected (selectedItems is empty), do nothing
      if (selectedItems.length === 0) {
        return;
      }
      onChange([]);
      return;
    }
    const isSelected = selectedItems.some(
      (selected) => getItemValue(selected) === getItemValue(item),
    );
    onChange(
      isSelected
        ? selectedItems.filter(
            (selected) => getItemValue(selected) !== getItemValue(item),
          )
        : [...selectedItems, item],
    );
  };

  const newPlaceholder = isAllSelected
    ? placeholder.concat(' (All)')
    : selectedItems.length > 1
    ? `${selectedItems.length} ${selectionPhrase}s selected`
    : selectedItems.length > 0
    ? `${
        selectedItems[0]
          ? getItemLabel(selectedItems[0])
          : `1 ${selectionPhrase} selected`
      }`
    : placeholder;

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          className={cn(
            'vai-justify-between vai-text-[#18181B] vai-font-normal vai-text-left',
            buttonClassName,
          )}
          variant="outline"
        >
          <p className="vai-text-ellipsis vai-overflow-hidden vai-whitespace-nowrap vai-max-w-[90%]">
            {newPlaceholder}
          </p>
          <FontAwesomeIcon
            className="vai-text-[#71717A] vai-flex-shrink-0"
            icon={faAngleDown}
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className={cn(
          'vai-w-72 vai-overflow-y-auto',
          buttonClassName === 'short'
            ? 'vai-max-h-[200px]'
            : 'vai-max-h-[65vh]',
        )}
      >
        <div className="vai-p-2">
          <Input
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearch(e.target.value)
            }
            className="vai-w-full"
          />
        </div>
        {!sortedItems.length && (
          <DropdownMenuLabel>{noItemsFoundLabel}</DropdownMenuLabel>
        )}
        {sortedItems.map((item) => {
          const isSelected =
            isAllSelected &&
            allOption &&
            getItemValue(item) === getItemValue(allOption)
              ? true
              : selectedItems.some(
                  (selected) => getItemValue(selected) === getItemValue(item),
                );
          return (
            <Button
              className="vai-w-full !vai-justify-start !vai-bg-inherit hover:!vai-bg-slate-100"
              key={getItemValue(item)}
              onClick={() => handleChange(item)}
            >
              <Checkbox
                className="vai-border-sapphire-600 data-[state=checked]:vai-bg-sapphire-600"
                checked={isSelected}
              />
              <p className="vai-text-black vai-text-ellipsis vai-overflow-hidden vai-whitespace-nowrap">
                {getItemLabel(item)}
              </p>
            </Button>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
